import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useRef } from 'react';
import { Box, Typography, IconButton, Paper } from '@mui/material';
import DeleteIcon from '../../public/DeleteIcon.svg';
import FileIcon from '../../public/FileIcon.svg';
import FileIconUpload from '../../public/FileIconUpload.svg';
import ErrorIcon from '../../public/Error.svg';
import { constants } from '../constants';
import { theme } from '@/styles/theme';
import { ConfigContext } from './ConfigContext';
const FilePicker = ({ onFilesChange, maxTotalSize = 10 * 1024 * 1024, // 10MB default
acceptedTypes = ['.png', '.jpg', '.jpeg', '.txt'], multiple = true }) => {
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [error, setError] = useState('');
    const fileInputRef = useRef(null);
    const { configState } = React.useContext(ConfigContext);
    const formatFileSize = (bytes) => {
        if (bytes === 0)
            return '0B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Number.parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + sizes[i];
    };
    const formatAcceptedTypes = (types) => {
        const cleanTypes = types.map((type) => type.startsWith('.')
            ? type.slice(1).toUpperCase()
            : type.toUpperCase());
        if (cleanTypes.length === 1) {
            return cleanTypes[0];
        }
        if (cleanTypes.length === 2) {
            return cleanTypes.join(', ');
        }
        const lastType = cleanTypes.pop();
        return cleanTypes.join(', ') + ', ' + constants.or + ' ' + lastType;
    };
    const validateFiles = (newFiles, isRemoved = false) => {
        const errors = [];
        const valid = [];
        for (const file of newFiles) {
            // Check file type
            const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
            if (!acceptedTypes.includes(fileExtension)) {
                errors.push(constants.file_picker_error_invalid_type(file.name, acceptedTypes.join(', ')));
                continue;
            }
            valid.push(file);
        }
        const currentFiles = selectedFiles.map((sf) => sf.file);
        const allFiles = isRemoved ? valid : [...currentFiles, ...valid];
        const totalSize = allFiles.reduce((sum, file) => sum + file.size, 0);
        if (totalSize > maxTotalSize) {
            const maxSizeFormatted = formatFileSize(maxTotalSize);
            errors.push(constants.file_picker_error_max_size(maxSizeFormatted));
            return { valid: [], errors };
        }
        return { valid, errors };
    };
    const handleFiles = (files) => {
        if (!files)
            return;
        const fileArray = Array.from(files);
        const { errors } = validateFiles(fileArray);
        if (errors.length > 0) {
            setError(errors.join('. '));
        }
        else {
            setError('');
        }
        const newSelectedFiles = fileArray.map((file, index) => ({
            file,
            id: `${file.name}-${Date.now()}-${index}`
        }));
        const updatedFiles = multiple
            ? [...selectedFiles, ...newSelectedFiles]
            : newSelectedFiles;
        setSelectedFiles(updatedFiles);
        onFilesChange?.(updatedFiles.map((sf) => sf.file), errors.length <= 0);
    };
    const handleDrop = (e) => {
        e.preventDefault();
        handleFiles(e.dataTransfer.files);
    };
    const handleDragOver = (e) => {
        e.preventDefault();
    };
    const handleDragLeave = (e) => {
        e.preventDefault();
    };
    const handleClick = () => {
        fileInputRef.current?.click();
    };
    const handleFileInputChange = (e) => {
        handleFiles(e.target.files);
    };
    const removeFile = (id) => {
        const updatedFiles = selectedFiles.filter((sf) => sf.id !== id);
        setSelectedFiles(updatedFiles);
        const { errors } = validateFiles(updatedFiles.map((sf) => sf.file), true);
        if (errors.length > 0) {
            setError(errors.join('. '));
        }
        else {
            setError('');
        }
        onFilesChange?.(updatedFiles.map((sf) => sf.file), errors.length <= 0);
    };
    const maxSizeFormatted = formatFileSize(maxTotalSize);
    return (_jsxs(Box, { sx: {
            width: '100%',
            maxWidth: 800,
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
        }, children: [_jsxs(Box, { children: [_jsxs(Paper, { elevation: 0, onClick: handleClick, onDrop: handleDrop, onDragOver: handleDragOver, onDragLeave: handleDragLeave, sx: {
                            border: error.length > 0
                                ? `1px dashed ${theme.custom.errorColor}`
                                : '1px dashed #d9d9d9',
                            borderRadius: 2,
                            p: 2,
                            textAlign: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease-in-out',
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: '#F8F8F8',
                            color: error.length > 0 ? '#9E9E9E' : 'inherit'
                        }, children: [_jsx(Box, { sx: {
                                    display: 'flex',
                                    width: '40px',
                                    height: '40px',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    borderRadius: '100px',
                                    opacity: 0.6,
                                    backgroundColor: '#EBEBEB',
                                    marginRight: '8px'
                                }, children: _jsx("img", { src: FileIconUpload, alt: "upload icon" }) }), _jsxs(Typography, { variant: "body2", sx: {
                                    display: 'flex'
                                }, children: [constants.drag_drop_upload, "\u00A0", _jsx(Box, { sx: {
                                            color: error.length > 0
                                                ? '#9E9E9E'
                                                : configState?.settings?.button_color,
                                            fontWeight: 600,
                                            textDecorationLine: 'underline',
                                            textDecorationStyle: 'solid'
                                        }, children: constants.browse_file })] }), _jsx(Typography, { color: error.length > 0 ? '#9E9E9E' : 'textSecondary', fontSize: "14px", ml: 'auto', children: constants.file_picker_supported_types_line(formatAcceptedTypes(acceptedTypes), maxSizeFormatted) })] }), _jsx("input", { ref: fileInputRef, type: "file", multiple: multiple, accept: acceptedTypes.join(','), onChange: handleFileInputChange, style: { display: 'none' }, disabled: error.length > 0 }), error && (_jsxs(Typography, { color: "error", variant: "caption", mt: '4px', sx: {
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                        }, "aria-label": "error-text", children: [_jsx("img", { src: ErrorIcon, alt: "error icon" }), error] }))] }), selectedFiles.length > 0 && (_jsx(Box, { sx: {
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px'
                }, children: selectedFiles.map((selectedFile) => (_jsxs(Box, { sx: {
                        display: 'flex',
                        padding: '12px',
                        alignItems: 'center',
                        gap: '8px',
                        borderRadius: '8px',
                        border: '1px solid #D9D9D9',
                        background: '#FFF'
                    }, children: [_jsx(Box, { sx: {
                                width: '24px',
                                height: '24px',
                                flexShrink: 0,
                                position: 'relative'
                            }, children: _jsx("img", { src: FileIcon, alt: "file icon" }) }), _jsxs(Box, { sx: {
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '4px',
                                flex: 1
                            }, children: [_jsxs(Box, { sx: {
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: '8px',
                                        justifyContent: 'space-between'
                                    }, children: [_jsx(Typography, { variant: "body2", children: selectedFile.file.name }), _jsx(Box, { sx: {
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px'
                                            }, children: _jsx(IconButton, { size: "small", "aria-label": "delete", onClick: () => removeFile(selectedFile.id), sx: {
                                                    width: '24px',
                                                    height: '24px',
                                                    padding: 0,
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(218, 41, 28, 0.04)'
                                                    }
                                                }, children: _jsx("img", { src: DeleteIcon, alt: "delete icon" }) }) })] }), _jsx(Typography, { variant: "caption", sx: {
                                        fontSize: '12px'
                                    }, children: formatFileSize(selectedFile.file.size) })] })] }, selectedFile.id))) }))] }));
};
export default FilePicker;
